import React, { useCallback, useEffect, useRef, useMemo, useState, useTransition } from 'react';
import {
  OwcDrawer,
  OwcSideMenu,
  OwcComponentHeader,
  OwcIconButton,
  OwcListItem,
  OwcIcon,
  OwcEditableDropdown,
  OwcSelectDropdown,
  OwcProgressBar,
} from '@one/react';

import { useTabContext } from '../../contexts/TabContext';
import MiddleContent from '../MainContainer/MiddleContent';
import { useGroupData, EMPTY_BASELINE_LISTS } from '../../contexts/GroupDataContext';
import { useNestedSubGroup } from '../../contexts/NestedSubGroupContext';
import { useAuth } from 'react-oidc-context';
import styles from './OwcDrawer.module.css';
import SectionAccordion from '../Accordion/SectionAccordion';
import { getDashboardSections } from '../../config/uiConfig';
import { useSchemaData } from '../../hooks/useSchemaFetcher';
import { useEntityVersions } from '../../hooks/useEntityVersions';
import { useFhirIg } from '../../hooks/useFhirIgs';
import { useDebouncedValue } from '../../hooks/useDebounceSearch';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  isLatestOnlyGroup,
  isIframeDomainGroup,
  getGroupUrlPathSegment,
  DEFAULT_GROUP,
} from '../../utils/getDataGroupPath';
import { findFhirIg, normalizeFhirIgs } from '../../utils/fhirIg/fhirIg';
import { getSchemaItems } from '../../utils/general/schemaEntity';
import { escapeRegExp, filterByName } from '../../utils/general/textSearch';
import { filterByChain, nextLevelOptions, maxDepth } from '../../utils/general/subGroupFilter';
import {
  useGetDataGroupsQuery,
  useLazyGetGroupDataQuery,
  useLazyGetReleasesQuery,
} from '../../rtk/api/datagroupsApi';

/** Stable empty list so a section with no configured source keeps its memo. */
const EMPTY_ITEMS = [];

/**
 * Lists that are filtered by the selected sub data group chain.
 *
 * `complexDataTypes` is deliberately absent: CDTs are group-scoped, not
 * subgroup-scoped, and are looked up by name alone elsewhere (TablePanel, the
 * URL-restore effect), so they stay unfiltered at every level.
 */
const CHAIN_FILTERED_LISTS = [
  'events',
  'resources',
  'fhirIgs',
  'requests',
  'responses',
  'commands',
  'acknowledgements',
  'types',
  'auxiliaries',
];

/**
 * Every entity list out of a group payload, defaulted and with the IGs
 * normalized so each carries its authoritative `parent`/`subGroup` — which is
 * what the chain filter matches on.
 */
function toBaselineLists(data) {
  const lists = { ...EMPTY_BASELINE_LISTS };
  for (const key of Object.keys(EMPTY_BASELINE_LISTS)) {
    lists[key] = Array.isArray(data?.[key]) ? data[key] : [];
  }
  lists.fhirIgs = normalizeFhirIgs(data?.fhirIgs);
  return lists;
}

/** Apply a sub data group chain to the untouched baseline lists. */
function applyChain(baseline, chain) {
  const scoped = { complexDataTypes: baseline.complexDataTypes };
  for (const key of CHAIN_FILTERED_LISTS) {
    scoped[key] = filterByChain(baseline[key], chain);
  }
  return scoped;
}

export default function OwcDrawerLayout({ drawerVisible, toggleDrawer, children }) {
  const [selectedGroup, setSelectedGroup] = useState(DEFAULT_GROUP);
  const [groupChangeLoading, setGroupChangeLoading] = useState(false);
  const editableDropdownElement = useRef(null);
  const { nestedChain, setNestedChain, selectAtLevel, clearFromLevel, clearAllLevels } =
    useNestedSubGroup();
  const location = useLocation();
  const auth = useAuth();
  const navigate = useNavigate();
  const isReleaseHistory = location.pathname.toLowerCase().includes('releasehistory');
  const isOverlayRoute =
    isReleaseHistory ||
    location.pathname.toLowerCase().includes('managerelease') ||
    location.pathname.toLowerCase().includes('schemacomparison') ||
    location.pathname.toLowerCase().includes('workflow');

  const { dataGroup, type, name, parentFromUrl } = useMemo(() => {
    const path = location.pathname.replace(/^\/dashboard\/?/, '');
    const segments = path.split('/').filter(Boolean);
    if (segments.length >= 3) {
      const parentParts = segments.slice(2, -1).map(decodeURIComponent);
      return {
        dataGroup: decodeURIComponent(segments[0]),
        type: decodeURIComponent(segments[1]),
        name: decodeURIComponent(segments[segments.length - 1]),
        parentFromUrl: parentParts.length > 0 ? parentParts.join('/') : '',
      };
    }
    return { dataGroup: undefined, type: undefined, name: undefined };
  }, [location.pathname]);

  const [releaseOptions, setReleaseOptions] = useState([]);
  const [releaseOptionsData, setReleaseOptionsData] = useState([]);
  const [releaseApiError, setReleaseApiError] = useState(null);
  const [isReleaseDisabled, setIsReleaseDisabled] = useState(false);
  const [loadingReleaseChange, setLoadingReleaseChange] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [hasCalledInitialChange, setHasCalledInitialChange] = useState(false);
  const [releaseFetched, setReleaseFetched] = useState(false);
  // Bumped only when baselineListsRef is actually (re)populated from a fetch —
  // distinct from groupData, which also changes identity on every subgroup
  // dropdown pick (applySubGroupFilter). Effects that read baselineListsRef
  // should key off this, not groupData, or they re-run on every filter pick.
  const [baselineVersion, setBaselineVersion] = useState(0);
  // Guards the one-time deep-link group restore so it never re-runs on later
  // navigation (which would wipe the user's selection).
  const deepLinkRestoredRef = useRef(false);
  // Entity key (type|name|parentFromUrl) the sub data group widening effect has
  // already resolved — see that effect for why this guard is needed.
  const widenedForKeyRef = useRef('');

  const {
    groupData,
    releaseVersion,
    setReleaseVersion,
    selectedSubGroup,
    setSelectedSubGroup,
    setGroupData,
    setAppDisabled,
    setLatestVersion,
    setReleaseNotes,
    lastDashboardPath,
    setLastDashboardPath,
    baselineListsRef,
  } = useGroupData();

  const {
    schemaData,
    schemaLoading,
    schemaError,
    schemaSource,
    fetchSchema,
    setSchemaError,
    setSchemaData,
    setSchemaSource,
  } = useSchemaData({ auth, groupName: groupData?.groupName, releaseVersion });

  // Per-entity schema versions for latest-only groups (e.g. DiabetesCare).
  // The selected version drives which schema is shown; it defaults to the
  // latest (versions[0]) whenever a new entity is selected.
  const latestOnlyGroup = isLatestOnlyGroup(groupData?.groupName);
  const {
    versions: entityVersions,
    versionsLoading: entityVersionsLoading,
    versionsError: entityVersionsError,
  } = useEntityVersions({
    auth,
    groupName: groupData?.groupName,
    latestOnly: latestOnlyGroup,
    schemaSource,
  });
  const [selectedEntityVersion, setSelectedEntityVersion] = useState('');

  // FHIR IG (iframe domain, e.g. Clinical): selecting a guide fetches its
  // artifacts (igUri) and publish-version list for the iframe view.
  const fhirIg = useFhirIg({
    auth,
    groupName: groupData?.groupName,
    urlPathSegment: getGroupUrlPathSegment(groupData?.groupName),
  });

  // Narrow the visible entity lists to a sub data group chain. Purely local:
  // the baseline holds every entity in the group, so this never refetches and
  // clearing a level restores the wider list instantly.
  const applySubGroupFilter = useCallback(
    (chain) => {
      setGroupData((prev) => ({
        ...(prev || {}),
        ...applyChain(baselineListsRef.current, chain),
      }));
    },
    [setGroupData, baselineListsRef]
  );

  // ── RTK Query hooks ──
  const { data: allGroups = [], isLoading: groupsLoading } = useGetDataGroupsQuery(undefined, {
    skip: !auth?.user?.id_token,
  });
  const [triggerGetGroupData] = useLazyGetGroupDataQuery();
  const [triggerGetReleases] = useLazyGetReleasesQuery();

  useEffect(() => {
    if (location.pathname.startsWith('/dashboard') && !isOverlayRoute) {
      setLastDashboardPath(location.pathname);
    }
  }, [location.pathname, isOverlayRoute, setLastDashboardPath]);

  // ── fetchReleaseVersions ──
  // `silent` populates the release list + latest version WITHOUT taking over the
  // current group (no `groupData` reset, no `hasCalledInitialChange`). It is used
  // during an iframe-domain deep-link restore so a later switch to a
  // release-versioned group (e.g. Lab) has a valid `releaseVersion` to fetch with.
  const fetchReleaseVersions = async ({ silent = false } = {}) => {
    if (!silent) {
      setIsInitialLoading(true);
      setGroupData({ groupName: selectedGroup, subGroups: [], events: [], resources: [] });
      setSelectedSubGroup('');
    }

    try {
      const { data, error } = await triggerGetReleases(undefined, false);
      if (error) throw new Error(error.message || 'Error loading release versions');

      if (typeof data !== 'object' || !data.groupName || !Array.isArray(data.releases)) {
        throw new Error('Invalid response: groupName or releases missing.');
      }

      const releaseVersions = data.releases.map((r) => r.releaseVersion).filter(Boolean);
      setReleaseOptions(releaseVersions);
      setReleaseOptionsData(data.releases);

      const semanticVersions = releaseVersions.filter((v) => /^\d+\.\d+\.\d+$/.test(v));
      const latest = semanticVersions.sort((a, b) =>
        b.localeCompare(a, undefined, { numeric: true, sensitivity: 'base' })
      )[0];

      setLatestVersion(latest || null);

      if (latest) {
        setReleaseVersion(latest);
        // In silent mode, don't trigger the default group's initial load.
        if (!silent) setHasCalledInitialChange(true);
        const latestRelease = data.releases.find((r) => r.releaseVersion === latest);
        setReleaseNotes(latestRelease?.releaseNotes || '');
      }

      if (!silent) {
        setGroupData({ groupName: data.groupName, subGroups: [], events: [], resources: [] });
      }
      setReleaseApiError(null);
    } catch (error) {
      setReleaseApiError(error.message);
      setIsReleaseDisabled(true);
      setAppDisabled(true);
      if (!silent) {
        setGroupData({ groupName: '', subGroups: [], events: [], resources: [] });
        setSelectedSubGroup('');
      }
    } finally {
      if (!silent) setIsInitialLoading(false);
    }
  };

  // ── handleReleaseChange ──
  const handleReleaseChange = async (event) => {
    const value = Array.isArray(event.detail) ? event.detail[0] : event.detail;

    if (!value) {
      setReleaseVersion('');
      setGroupData({});
      fetchReleaseVersions();
      return;
    }

    setLoadingReleaseChange(true);
    setReleaseApiError(null);
    setReleaseVersion(value);
    setSchemaData(null);
    setSchemaError(null);
    setSchemaSource(null);
    setActiveSchemaKey(null);
    setActiveParent('');

    try {
      const selectedRelease = releaseOptionsData?.find((r) => r.releaseVersion === value);
      setReleaseNotes(selectedRelease?.releaseNotes || '');

      const groupName = groupData?.groupName;
      if (!groupName) return;

      const { data, error } = await triggerGetGroupData(
        {
          groupName,
          releaseVersion: value,
          latest: isLatestOnlyGroup(groupName),
        },
        false
      );

      if (error) throw new Error(error.message || `schema fetch failed for ${value}`);

      if (
        typeof data !== 'object' ||
        !data.groupName ||
        !Array.isArray(data.subGroups) ||
        !Array.isArray(data.events)
      ) {
        throw new Error('Unexpected schema response structure.');
      }

      // This is the initial-load path for the default group, so the baseline
      // every sub data group filter reads from is captured here.
      const baseline = toBaselineLists(data);
      baselineListsRef.current = baseline;
      setBaselineVersion((v) => v + 1);
      setGroupData({ ...data, ...baseline });

      // Never reset the URL for iframe-domain items (FHIR IGs): they are not in
      // this group's schema lists and are resolved via the deep-link restore.
      if (type && name && type !== 'fhirIg') {
        let found = false;
        if (type === 'event') found = data.events?.some((e) => e.name === name);
        else if (type === 'resource') found = data.resources?.some((r) => r.name === name);
        else if (type === 'complexDataType')
          found = data.complexDataTypes?.some((c) => c.name === name);
        if (!found) {
          setLastDashboardPath('/dashboard');
          navigate('/dashboard');
        }
      }
    } catch (err) {
      setReleaseApiError(`Error: ${err.message}`);
      setGroupData({});
    } finally {
      setLoadingReleaseChange(false);
    }
  };

  // Trigger initial release change after fetch
  useEffect(() => {
    if (hasCalledInitialChange && releaseVersion) {
      handleReleaseChange({ detail: releaseVersion });
      setHasCalledInitialChange(false);
    }
  }, [hasCalledInitialChange, releaseVersion]);

  // Listen for clearReleaseSelection event
  useEffect(() => {
    const handler = () => {
      setReleaseVersion('');
      setGroupData({ groupName: '', subGroups: [], events: [], resources: [] });
      setSelectedSubGroup('');
    };
    window.addEventListener('clearReleaseSelection', handler);
    return () => window.removeEventListener('clearReleaseSelection', handler);
  }, []);

  // Re-fetch releases after a new release is created
  useEffect(() => {
    const handler = () => fetchReleaseVersions();
    window.addEventListener('refreshReleases', handler);
    return () => window.removeEventListener('refreshReleases', handler);
  }, []);

  // A cold load of an iframe-domain deep link (e.g.
  // `/dashboard/Clinical/fhirIg/BaseDataModel/bdm-r4`) must restore that group
  // instead of the default. This is only knowable once the URL is parsed, which
  // can happen a render after auth resolves — so it lives in its own effect
  // keyed on `dataGroup`, guarded by a one-shot ref, independent of the default
  // release flow's `releaseFetched` gate.
  const hasPendingDeepLink =
    !!dataGroup && dataGroup !== DEFAULT_GROUP && isIframeDomainGroup(dataGroup);

  useEffect(() => {
    if (!auth?.user?.id_token) return;
    if (deepLinkRestoredRef.current || !hasPendingDeepLink) return;

    // Iframe-domain groups are latest-only: no release version needed to load
    // them. `handleGroupChange` loads the group; the URL-driven effect then
    // selects the FHIR IG. `preserveUrl` keeps type/name/parent in the address
    // bar. We also fetch release versions silently so a later switch to a
    // release-versioned group (e.g. Lab) has a valid `releaseVersion`.
    deepLinkRestoredRef.current = true;
    setReleaseFetched(true); // suppress the default (non-silent) flow below
    setIsInitialLoading(false);
    fetchReleaseVersions({ silent: true });
    handleGroupChange({ detail: dataGroup }, { preserveUrl: true });
    // Handler identities are stable for this one-time restore.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.user?.id_token, hasPendingDeepLink, dataGroup]);

  // Default flow: fetch release versions for the default group on auth. Skipped
  // when an iframe-domain deep link is being restored above.
  useEffect(() => {
    if (!auth?.user?.id_token || releaseFetched) return;
    if (hasPendingDeepLink) return; // handled by the deep-link effect
    setReleaseFetched(true);
    fetchReleaseVersions();
    // fetchReleaseVersions identity is stable for this one-time init.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.user?.id_token, releaseFetched, hasPendingDeepLink]);

  // Focus handler for release dropdown
  useEffect(() => {
    function handler() {
      const el = document.getElementById('release-version-anchor');
      if (el) {
        el.focus({ preventScroll: true });
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 10);
      }
    }
    window.addEventListener('focus-release-dropdown', handler);
    return () => window.removeEventListener('focus-release-dropdown', handler);
  }, []);

  // ── handleGroupChange ──
  // `preserveUrl` keeps the current dashboard URL instead of resetting to
  // `/dashboard`. It is used when restoring a group from a deep link so the
  // URL-driven auto-select (e.g. a FHIR IG) still has its type/name/parent.
  const handleGroupChange = async (event, { preserveUrl = false } = {}) => {
    const value = Array.isArray(event.detail) ? event.detail[0] : event.detail;

    setSelectedGroup(value);
    setSelectedSubGroup('');
    clearAllLevels();
    setGroupData({
      groupName: value,
      subGroups: [],
      events: [],
      resources: [],
      complexDataTypes: [],
    });
    setSchemaData(null);
    setSchemaError(null);
    setSchemaSource(null);
    setActiveSchemaKey(null);
    setActiveParent('');
    if (!preserveUrl) navigate('/dashboard');
    setGroupChangeLoading(true);

    try {
      const { data, error } = await triggerGetGroupData(
        {
          groupName: value,
          releaseVersion,
          latest: isLatestOnlyGroup(value),
        },
        false
      );

      if (error) throw new Error(error.message || 'Failed to fetch group data');

      const baseline = toBaselineLists(data);
      const normalized = {
        ...data,
        groupName: data.groupName || value,
        subGroups: data.subGroups || [],
        ...baseline,
      };

      baselineListsRef.current = baseline;
      setBaselineVersion((v) => v + 1);
      setGroupData(normalized);
    } catch (err) {
      console.error(err);
    } finally {
      setGroupChangeLoading(false);
    }
  };

  // ── Drawer UI state ──
  const subGroups = useMemo(() => groupData?.subGroups || [], [groupData?.subGroups]);
  const events = groupData?.events || [];
  const resources = groupData?.resources || [];
  const complexDataTypes = groupData?.complexDataTypes || [];
  const fhirIgs = groupData?.fhirIgs || [];
  const requests = groupData?.requests || [];
  const responses = groupData?.responses || [];
  const commands = groupData?.commands || [];
  const acknowledgements = groupData?.acknowledgements || [];
  const types = groupData?.types || [];
  const auxiliaries = groupData?.auxiliaries || [];
  const groupName = groupData?.groupName || '';

  const [openSection, setOpenSection] = useState(
    localStorage.getItem('drawer_openSection') || 'events'
  );
  const [activeSchemaKey, setActiveSchemaKey] = useState(null);
  const [activeParent, setActiveParent] = useState('');
  const [isPending, startTransition] = useTransition();
  const { activeTab } = useTabContext();
  const [searchTerm, setSearchTerm] = useState('');
  const forceUpdate = useState(0)[1];
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);

  // The full sub data group selection: no selection means no filter at all.
  const selectedChain = useMemo(
    () => (!selectedSubGroup ? [] : [selectedSubGroup, ...nestedChain]),
    [selectedSubGroup, nestedChain]
  );

  // Every chain-filterable entity, unfiltered — the source the nested dropdown
  // options are derived from. The baseline ref is only ever rewritten next to a
  // `setGroupData`, so `groupData` is its render signal.
  const baselineEntities = useMemo(
    () => CHAIN_FILTERED_LISTS.flatMap((key) => baselineListsRef.current[key] || []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groupData, baselineListsRef]
  );

  // Nested dropdowns, derived rather than fetched: one level per depth actually
  // present in the data, stopping at the deepest level the user has chosen.
  const nestedLevels = useMemo(() => {
    if (!selectedSubGroup) return [];

    const levels = [];
    const depth = maxDepth(baselineEntities);
    let chain = [selectedSubGroup];

    for (let level = 0; level + 1 < depth; level++) {
      const options = nextLevelOptions(baselineEntities, chain);
      if (!options.length) break;
      const selected = nestedChain[level] || '';
      levels.push({ options, selected });
      if (!selected) break;
      chain = [...chain, selected];
    }

    return levels;
  }, [baselineEntities, selectedSubGroup, nestedChain]);

  // Selecting a subgroup that holds nothing used to surface an error from the
  // fetch; with filtering there is no request to fail, so say it plainly here
  // rather than leaving the drawer silently blank.
  const subGroupEmptyMessage = useMemo(() => {
    // Mid-fetch, baselineEntities/selectedChain can be transiently stale or
    // empty (e.g. a group switch resets groupData before the new baseline
    // lands) — a real "empty" verdict is only trustworthy once settled.
    if (isInitialLoading || groupChangeLoading || loadingReleaseChange) return '';
    if (!selectedChain.length) return '';
    if (filterByChain(baselineEntities, selectedChain).length > 0) return '';
    return isLatestOnlyGroup(selectedGroup) ? 'No items found.' : 'No events or resources found.';
  }, [
    baselineEntities,
    selectedChain,
    selectedGroup,
    isInitialLoading,
    groupChangeLoading,
    loadingReleaseChange,
  ]);

  useEffect(() => {
    if (!/^\/dashboard\/?$/.test(location.pathname)) return;
    setSchemaData(null);
    setSchemaError(null);
    setSchemaSource(null);
    setActiveSchemaKey(null);
    setActiveParent('');
    fhirIg.reset();
  }, [location.pathname, setSchemaData, setSchemaError, setSchemaSource, fhirIg.reset]);

  useEffect(() => {
    forceUpdate((n) => n + 1);
  }, [activeTab]);

  useEffect(() => {
    if (!isOverlayRoute && !drawerVisible) toggleDrawer();
  }, [isOverlayRoute]);

  // Default to the unfiltered view whenever a new group/release loads. The
  // nested levels go with it: their options came from the previous baseline.
  useEffect(() => {
    if (subGroups.length === 0) return;
    setSelectedSubGroup('');
    clearAllLevels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [releaseVersion, subGroups]);

  useEffect(() => {
    if (
      events.length === 0 &&
      resources.length === 0 &&
      complexDataTypes.length === 0 &&
      requests.length === 0 &&
      responses.length === 0 &&
      commands.length === 0 &&
      acknowledgements.length === 0 &&
      types.length === 0 &&
      auxiliaries.length === 0
    ) {
      setSchemaData(null);
      setSchemaError(null);
      setSchemaSource(null);
      setActiveSchemaKey(null);
      setActiveParent('');
    }
  }, [
    acknowledgements,
    auxiliaries,
    commands,
    complexDataTypes,
    events,
    requests,
    resources,
    responses,
    types,
  ]);

  // Stable identity: SectionAccordion is memoized, so a new callback per render
  // would re-render every section on any drawer state change.
  const toggleSection = useCallback((sectionKey) => {
    setOpenSection((prev) => (prev === sectionKey ? null : sectionKey));
  }, []);

  useEffect(() => {
    localStorage.setItem('drawer_openSection', openSection || '');
  }, [openSection]);

  // A nested level changed. `selected` is falsy when the level's clear icon was
  // used (OWC emits an empty array, which unwraps to `undefined`), in which
  // case this level and everything below it drop out of the chain.
  const handleNestedSubGroupChange = (levelIndex, selected) => {
    if (!selectedSubGroup) return;

    const chain = [selectedSubGroup, ...nestedChain.slice(0, levelIndex)];
    if (selected) {
      selectAtLevel(levelIndex, selected);
      chain.push(selected);
    } else {
      clearFromLevel(levelIndex);
    }

    applySubGroupFilter(chain);
  };

  // Highlights the debounced term (the one the lists are actually filtered by)
  // and escapes it: the raw term is user input and `(`, `[`, `*` … would
  // otherwise throw while constructing the RegExp and take the drawer down.
  const highlightMatch = useCallback(
    (text) => {
      if (!debouncedSearchTerm) return text;
      const parts = String(text).split(new RegExp(`(${escapeRegExp(debouncedSearchTerm)})`, 'gi'));
      return parts.map((part, i) =>
        part.toLowerCase() === debouncedSearchTerm.toLowerCase() ? (
          <mark key={i}>{part}</mark>
        ) : (
          part
        )
      );
    },
    [debouncedSearchTerm]
  );

  // Resolve the item list for each configured section by its `itemsSource` key.
  // Every source is filtered by the search term — a group such as MolLab is
  // mostly requests/responses/types/auxiliaries, and leaving those unfiltered
  // means search cannot shrink the list at all.
  const sectionItemsBySource = useMemo(
    () => ({
      events: filterByName(events, debouncedSearchTerm),
      resources: filterByName(resources, debouncedSearchTerm),
      complexDataTypes: filterByName(complexDataTypes, debouncedSearchTerm),
      fhirIgs: filterByName(fhirIgs, debouncedSearchTerm),
      requests: filterByName(requests, debouncedSearchTerm),
      responses: filterByName(responses, debouncedSearchTerm),
      commands: filterByName(commands, debouncedSearchTerm),
      acknowledgements: filterByName(acknowledgements, debouncedSearchTerm),
      types: filterByName(types, debouncedSearchTerm),
      auxiliaries: filterByName(auxiliaries, debouncedSearchTerm),
    }),
    [
      acknowledgements,
      auxiliaries,
      commands,
      complexDataTypes,
      debouncedSearchTerm,
      events,
      fhirIgs,
      requests,
      resources,
      responses,
      types,
    ]
  );

  const latestOnly = isLatestOnlyGroup(selectedGroup);
  // Sections are resolved per domain/group, falling back to the default list.
  const visibleSections = useMemo(() => getDashboardSections(selectedGroup), [selectedGroup]);

  // A deep link can point at an entity outside the current sub data group
  // filter — e.g. following a link to a Sample/Processing event while Algorithm
  // is selected. The filtered lists genuinely do not contain it, so widen the
  // selection to the entity's own chain before the lookups below run.
  //
  // Only once per URL entity: `groupData` is a new object on every subgroup
  // dropdown change too (applySubGroupFilter re-sets it), so without this
  // guard the effect would re-widen on every pick and fight the dropdowns —
  // this ref remembers the entity key already resolved so it only acts again
  // once the URL actually points at a different entity.
  useEffect(() => {
    const entityKey = `${type}|${name}|${parentFromUrl}`;
    if (!type || !name || type === 'complexDataType') return;
    if (widenedForKeyRef.current === entityKey) return;
    if (getSchemaItems(groupData, type).some((i) => i?.name === name)) {
      widenedForKeyRef.current = entityKey;
      return;
    }
    if (type === 'fhirIg' && findFhirIg(fhirIgs, name, parentFromUrl)) {
      widenedForKeyRef.current = entityKey;
      return;
    }

    const baseline = baselineListsRef.current;
    const list = type === 'fhirIg' ? baseline.fhirIgs : getSchemaItems(baseline, type);
    const match = parentFromUrl
      ? list.find((i) => i?.name === name && i?.parent === parentFromUrl) ||
        list.find((i) => i?.name === name)
      : list.find((i) => i?.name === name);

    const chain = (match?.parent || parentFromUrl || '').split('/').filter(Boolean);
    if (!chain.length || chain.join('/') === selectedChain.join('/')) return;

    widenedForKeyRef.current = entityKey;
    setSelectedSubGroup(chain[0]);
    setNestedChain(chain.slice(1));
    applySubGroupFilter(chain);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, name, parentFromUrl, groupData, fhirIgs]);

  // URL-driven active key
  useEffect(() => {
    if (!type || !name) return;
    if (type !== 'complexDataType' && type !== 'fhirIg') {
      const list = getSchemaItems(groupData, type);
      const item = parentFromUrl
        ? list.find((e) => e.name === name && e.parent === parentFromUrl) ||
          list.find((e) => e.name === name)
        : list.find((e) => e.name === name);
      if (item?.parent) {
        setActiveSchemaKey(`${type}-${item.parent}/${name}`);
        setActiveParent(item.parent);
        return;
      }
    }
    if (type === 'complexDataType') {
      const item = complexDataTypes.find((c) => c.name === name);
      if (item?.parent) {
        setActiveSchemaKey(`${type}-${item.parent}/${name}`);
        return;
      }
    }
    if (type === 'fhirIg') {
      const item = findFhirIg(fhirIgs, name, parentFromUrl);
      if (item?.parent) {
        setActiveSchemaKey(`${type}-${item.parent}/${name}`);
        setActiveParent(item.parent);
        return;
      }
    }
    setActiveSchemaKey(`${type}-${name}`);
  }, [type, name, parentFromUrl, groupData, complexDataTypes, fhirIgs]);

  // Auto-select a FHIR IG (iframe domain) on URL change: fetch its artifacts
  // (igUri) and version list. This replaces the schema fetch for these groups.
  useEffect(() => {
    if (type !== 'fhirIg' || !name) return;
    const item = findFhirIg(fhirIgs, name, parentFromUrl || activeParent);
    if (!item) return;
    const subGroupChain = (item.parent || parentFromUrl || '').split('/').filter(Boolean);
    if (!subGroupChain.length) return;
    if (
      fhirIg.selectedIg?.guide === name &&
      fhirIg.selectedIg?.subGroupChain?.join('/') === subGroupChain.join('/')
    )
      return;
    fhirIg.selectIg({ guide: name, subGroupChain, version: item.publishVersion || undefined });
    // fhirIg.selectIg identity is stable; re-run only when the target IG changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, name, parentFromUrl, activeParent, fhirIgs]);

  // Auto-fetch schema on URL/release change.
  //
  // Looks the entity up in the unfiltered baseline, not `groupData`: `groupData`
  // gets a new identity on every sub data group dropdown pick too
  // (applySubGroupFilter), which previously made this effect re-fetch on every
  // such pick even though type/name/activeParent/releaseVersion never changed.
  // It also meant a duplicate name across subgroups resolved to whichever
  // filtered list's `matches[0]` happened to be in view, so the same entity
  // could silently swap between its duplicates depending on the subgroup
  // filter. The baseline's order is stable regardless of the filter, so
  // `matches[0]` is now deterministic; `baselineVersion` is the dependency
  // that actually means "the baseline changed".
  useEffect(() => {
    if (loadingReleaseChange) return;
    // Iframe-domain items (FHIR IGs) resolve via useFhirIg, not the schema fetch.
    if (type === 'fhirIg') return;
    if (!type || !name || !releaseVersion) return;
    const baseline = baselineListsRef.current;
    let parent = '';
    let version = null;

    if (type === 'complexDataType') {
      const cdts = baseline.complexDataTypes?.filter((c) => c.name === name) || [];
      const cdt = activeParent ? cdts.find((c) => c.parent === activeParent) || cdts[0] : cdts[0];
      if (!cdt) return;
      parent = cdt.parent || '';
      version = cdt.version || null;
    }

    if (type !== 'complexDataType') {
      const list = getSchemaItems(baseline, type);
      if (!Array.isArray(list) || list.length === 0) return;
      const matches = list.filter((e) => e.name === name);
      const entity = activeParent
        ? matches.find((e) => e.parent === activeParent) || matches[0]
        : matches[0];
      if (!entity) return;
      parent = entity.parent || '';
      version = entity.version || null;
    }

    if (!version) return;
    fetchSchema({ type, name, parent, version });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, name, activeParent, releaseVersion, loadingReleaseChange, baselineVersion]);

  // Default the entity version selector to the latest (newest-first list) each
  // time a new version list arrives for a newly selected entity.
  useEffect(() => {
    setSelectedEntityVersion(entityVersions[0] || '');
  }, [entityVersions]);

  // Re-fetch the selected entity's schema at a chosen version (latest-only
  // groups). schemaSource carries the entity identity; only the version varies.
  const handleEntityVersionChange = useCallback(
    (event) => {
      const value = Array.isArray(event.detail) ? event.detail[0] : event.detail;
      if (!value || !schemaSource?.type || !schemaSource?.name) return;
      setSelectedEntityVersion(value);
      fetchSchema({
        type: schemaSource.type,
        name: schemaSource.name,
        parent: schemaSource.parent,
        version: value,
      });
    },
    [schemaSource, fetchSchema]
  );

  return (
    <div className={styles.drawerRoot}>
      <OwcDrawer
        disableEscapeKeydown
        visible
        mode="push"
        placement="left"
        hideBackdrop
        className={styles.drawer}
      >
        {!isOverlayRoute ? (
          <OwcSideMenu
            slot="panel"
            className={`${styles.sideMenu} ${!drawerVisible ? styles.sideMenuCollapsed : ''}`}
          >
            {!drawerVisible && !isOverlayRoute && (
              <div className={styles.toggleArea}>
                <div className={styles.toggleButton} onClick={toggleDrawer}>
                  <OwcIcon name="chevron_right" family="outlined" style={{ fontSize: 18 }} />
                </div>
              </div>
            )}

            {drawerVisible && (
              <>
                <OwcComponentHeader slot="header" className={styles.header} separator>
                  <div className={styles.headerRow}>
                    <OwcSelectDropdown
                      size="medium"
                      value={selectedGroup}
                      placeholder="Select Data Group"
                      onValueChange={handleGroupChange}
                      disabled={groupsLoading || groupChangeLoading}
                      style={{ minWidth: '160px' }}
                      noClearIcon="true"
                    >
                      {allGroups.map((grp, idx) => (
                        <OwcListItem key={idx} value={grp?.groupName}>
                          {grp?.alias}
                        </OwcListItem>
                      ))}
                    </OwcSelectDropdown>
                    <OwcIconButton icon="clear" family="outlined" onClick={toggleDrawer} />
                  </div>
                </OwcComponentHeader>

                <div className={styles.content}>
                  {groupChangeLoading && (
                    <div style={{ padding: '16px 0', textAlign: 'center' }}>
                      <OwcProgressBar
                        caption="Loading data group..."
                        value={undefined}
                        size="small"
                        style={{ width: '100%' }}
                      />
                    </div>
                  )}
                  {!groupChangeLoading && !isLatestOnlyGroup(selectedGroup) && (
                    <>
                      {releaseApiError && (
                        <div className={styles.releaseApiError}>
                          <owc-banner status="warning" role="alert">
                            {releaseApiError}
                          </owc-banner>
                        </div>
                      )}
                      {!releaseApiError && !isInitialLoading && (
                        <div className={styles.dropdownWrapper}>
                          <div style={{ position: 'relative', width: '100%' }}>
                            <span
                              id="release-version-anchor"
                              tabIndex="-1"
                              style={{ display: 'block', height: 0, width: 0, outline: 'none' }}
                            ></span>
                            <OwcSelectDropdown
                              className={styles.owcdrawer_subgroup_dropdown}
                              label="Release version"
                              limit={3}
                              size="medium"
                              placeholder="Release version"
                              value={releaseVersion}
                              onValueChange={handleReleaseChange}
                              disabled={isReleaseDisabled}
                              no-clear-icon
                              style={{ width: '100%' }}
                            >
                              {releaseOptions.map((release, index) => (
                                <OwcListItem key={index} value={release}>
                                  {release}
                                </OwcListItem>
                              ))}
                            </OwcSelectDropdown>
                            {loadingReleaseChange && (
                              <div className={styles.releaseProgressBar}>
                                <OwcProgressBar
                                  value={undefined}
                                  size="small"
                                  color="info"
                                  caption={null}
                                  style={{
                                    height: '100%',
                                    width: '100%',
                                    background: 'transparent',
                                    margin: 0,
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      {isInitialLoading && (
                        <div style={{ padding: '8px 0' }}>
                          <OwcProgressBar
                            caption="Loading releases..."
                            value={undefined}
                            size="small"
                            style={{ width: '100%' }}
                          />
                        </div>
                      )}
                    </>
                  )}
                  {!groupChangeLoading && (
                    <div className={styles.dropdownWrapper}>
                      <div style={{ display: 'flex', flexDirection: 'column', maxWidth: '100%' }}>
                        <OwcEditableDropdown
                          className={styles.owcdrawer_subgroup_dropdown}
                          ref={editableDropdownElement}
                          disabled={!releaseVersion && !isLatestOnlyGroup(selectedGroup)}
                          label="Sub Data Group 1"
                          size="medium"
                          placeholder="Select Sub Data Group 1"
                          validity={{ state: 'valid' }}
                          value={selectedSubGroup}
                          onValueChange={(e) => {
                            const value = Array.isArray(e.detail) ? e.detail[0] : e.detail;
                            // Clearing the dropdown means no filter, restoring the baseline.
                            const next = value || '';
                            setSelectedSubGroup(next);
                            clearAllLevels();
                            startTransition(() => {
                              applySubGroupFilter(next ? [next] : []);
                            });
                          }}
                        >
                          {subGroups.map((value, index) => (
                            <OwcListItem key={index} value={value}>
                              <div>{value}</div>
                            </OwcListItem>
                          ))}
                        </OwcEditableDropdown>
                        {nestedLevels.map((level, levelIndex) => (
                          <div key={levelIndex} style={{ marginTop: '12px' }}>
                            <OwcSelectDropdown
                              className={styles.owcdrawer_subgroup_dropdown}
                              label={`Sub Data Group ${levelIndex + 2}`}
                              limit={3}
                              size="medium"
                              placeholder={`Sub Data Group ${levelIndex + 2}`}
                              value={level.selected}
                              onValueChange={(event) => {
                                const val = Array.isArray(event.detail)
                                  ? event.detail[0]
                                  : event.detail;
                                handleNestedSubGroupChange(levelIndex, val);
                              }}
                              disabled={!releaseVersion && !isLatestOnlyGroup(selectedGroup)}
                            >
                              {level.options.map((item, idx) => (
                                <OwcListItem key={idx} value={item}>
                                  {item}
                                </OwcListItem>
                              ))}
                            </OwcSelectDropdown>
                          </div>
                        ))}
                        {subGroupEmptyMessage && (
                          <div className={styles.subgroup2_error}>{subGroupEmptyMessage}</div>
                        )}
                      </div>
                    </div>
                  )}
                  <div>
                    <input
                      className={styles.owcdrawer_search_input}
                      type="text"
                      noClearIcon="false"
                      placeholder={
                        isIframeDomainGroup(selectedGroup)
                          ? 'Search Guides'
                          : isLatestOnlyGroup(selectedGroup)
                            ? 'Search Resources'
                            : 'Search Events/Resources'
                      }
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <div
                    className={styles.scrollableListArea}
                    style={{ display: 'flex', flexDirection: 'column' }}
                  >
                    {visibleSections.map((section, index) => {
                      const accordion = (
                        <SectionAccordion
                          title={section.title}
                          items={sectionItemsBySource[section.itemsSource] || EMPTY_ITEMS}
                          visible={openSection === section.id}
                          sectionId={section.id}
                          onToggle={toggleSection}
                          type={section.type}
                          activeKey={activeSchemaKey}
                          setActiveKey={setActiveSchemaKey}
                          setActiveParent={setActiveParent}
                          highlight={section.enableHighlight ? highlightMatch : null}
                          groupName={selectedGroup}
                          {...(section.needsSchemaSetters
                            ? { setSchemaError, setSchemaData, setSchemaSource }
                            : {})}
                        />
                      );

                      return (
                        <React.Fragment key={section.id}>
                          {index > 0 && <div style={{ height: 14 }} />}
                          {section.grow ? (
                            <div style={{ flex: latestOnly ? '1 1 auto' : undefined }}>
                              {accordion}
                            </div>
                          ) : (
                            accordion
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </OwcSideMenu>
        ) : (
          <div slot="panel" style={{ width: 0, overflow: 'hidden' }} />
        )}

        <div className={styles.scrollPane}>
          {children}
          {!isOverlayRoute && (
            <MiddleContent
              groupName={groupName}
              schemaData={schemaData}
              schemaLoading={schemaLoading}
              schemaError={schemaError}
              schemaSource={schemaSource}
              loadingInitialRelease={isInitialLoading}
              fetchReleaseVersions={fetchReleaseVersions}
              groupChangeLoading={groupChangeLoading}
              loadingReleaseChange={loadingReleaseChange}
              entityVersions={entityVersions}
              entityVersionsLoading={entityVersionsLoading}
              entityVersionsError={entityVersionsError}
              selectedEntityVersion={selectedEntityVersion}
              onEntityVersionChange={handleEntityVersionChange}
              igUri={fhirIg.igUri}
              igMeta={fhirIg.igMeta}
              igLoading={fhirIg.igLoading}
              igError={fhirIg.igError}
              igVersions={fhirIg.versions}
              igVersionsLoading={fhirIg.versionsLoading}
              selectedIgVersion={fhirIg.selectedVersion}
              onIgVersionChange={fhirIg.changeVersion}
              hasIgSelection={fhirIg.hasSelection}
            />
          )}
        </div>
      </OwcDrawer>
    </div>
  );
}
